import axios from 'axios'
import apiClient from '@/shared/services/apiClient'
import type {
  OnboardUserRequest,
  OnboardUserResponse,
  UpdateProfileMetadataRequest,
  ApiResponse,
  UserResponse,
  CheckEmailResponse,
  CheckPhoneResponse,
} from '../types'

class UserService {
  private readonly baseUrl = '/api/v1/users'

  /**
   * Check if an email is available for student signup/login.
   * This is a public/open endpoint — uses plain axios (no auth interceptors).
   *
   * @param email Email address to check
   * @returns CheckEmailResponse with exists, accountType, and studentSignupAllowed flags
   */
  async checkEmailAvailability(email: string): Promise<CheckEmailResponse> {
    const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
    const response = await axios.get<{ success: boolean; data: CheckEmailResponse }>(
      `${baseURL}${this.baseUrl}/check-email`,
      { params: { email } }
    )
    return response.data.data
  }

  /**
   * Check if a phone number is available for student signup/login.
   * This is a public/open endpoint — uses plain axios (no auth interceptors).
   *
   * @param phoneNumber Phone number in E.164 format (e.g. +919876543210)
   * @returns CheckPhoneResponse with exists, accountType, and studentSignupAllowed flags
   */
  async checkPhoneAvailability(phoneNumber: string): Promise<CheckPhoneResponse> {
    const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
    const response = await axios.get<{ success: boolean; data: CheckPhoneResponse }>(
      `${baseURL}${this.baseUrl}/check-phone`,
      { params: { phoneNumber } }
    )
    return response.data.data
  }

  /**
   * Onboard user after Cognito authentication.
   * This is an idempotent endpoint - safe to call on every login.
   *
   * - If user doesn't exist: Creates new user from JWT claims + request body
   * - If user exists: Returns existing user
   *
   * @param data Optional profile data to include during onboarding
   * @returns OnboardUserResponse with isNewUser and profileComplete flags
   */
  async onboardUser(data?: OnboardUserRequest): Promise<OnboardUserResponse> {
    const response = await apiClient.post<OnboardUserResponse>(
      `${this.baseUrl}/onboard`,
      data || {}
    )
    return response.data
  }

  /**
   * Get user by ID
   *
   * @param userId The user's UUID
   */
  async getUserById(userId: string): Promise<ApiResponse<UserResponse>> {
    const response = await apiClient.get<ApiResponse<UserResponse>>(`${this.baseUrl}/${userId}`)
    return response.data
  }

  /**
   * Update only the profile metadata (NEET student specific data)
   *
   * @param userId The user's UUID
   * @param data Profile metadata to update
   */
  async updateProfileMetadata(
    userId: string,
    data: UpdateProfileMetadataRequest
  ): Promise<ApiResponse<UserResponse>> {
    const response = await apiClient.put<ApiResponse<UserResponse>>(
      `${this.baseUrl}/${userId}/metadata`,
      data
    )
    return response.data
  }
}

export default new UserService()
